import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { API_BASE_URL } from '@synergy/config';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { BonusSettlementsSupplierOverviewApi } from '../../api/bonus-settlements-supplier-overview-api';

const api = new BonusSettlementsSupplierOverviewApi(API_BASE_URL);

export const fetchBonusSettlementsSupplierOverview = createAsyncThunk(
    'bonus-settlements/fetchBonusSettlementsSupplierOverview',
    async (params: { id: number; searchParams: URLSearchParams }, thunkAPI) => {
        const response = await api.getBonusSettlementsSupplierOverview(params.id, params.searchParams);

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }
        return response.result;
    }
);

export const fetchBonusSettlementsSupplierOverviewDetails = createAsyncThunk(
    'bonus-settlements/fetchBonusSettlementsSupplierOverviewDetails',
    async (id: number, thunkAPI) => {
        const response = await api.getBonusSettlementsSupplierOverviewDetails(id);

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }
        return response.result;
    }
);

export const resetBonusSettlementsSupplierOverviewState = createAsyncThunk(
    'bonus-settlements/resetBonusSettlementsSupplierOverviewState',
    async () => {
        return null;
    }
);

// Top-Bar Actions
export const addSupplier = createAsyncThunk(
    'bonus-settlements/addSupplier',
    async (params: { bonusSettlementId: number; supplierId: number }, thunkAPI) => {
        const response = await api.postAddBonusSettlementSupplier(params.bonusSettlementId, params.supplierId);

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        if (!response.result) {
            thunkAPI.dispatch(
                Snackbar.enqueue(
                    PoolNotification.warning('app.bonusSettlementsSupplierOverview.action.addSupplier.snackbar.warning')
                )
            );

            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(
                PoolNotification.success('app.bonusSettlementsSupplierOverview.action.addSupplier.snackbar.success')
            )
        );
        return response.result;
    }
);

export const inviteSupplier = createAsyncThunk(
    'bonus-settlements/inviteSuppliers',
    async (bonusSettlementId: number, thunkAPI) => {
        const response = await api.postInviteBonusSettlementSuppliers(bonusSettlementId);

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(PoolNotification.success('app.bonusSettlementsSupplierOverview.sendToSupplier.snackbar'))
        );

        return response.result;
    }
);

// Table Actions
export const deleteSupplier = createAsyncThunk(
    'bonus-settlements/deleteSupplier',
    async (params: { bonusSettlementId: number; bonusSettlementSupplierId: number }, thunkAPI) => {
        const response = await api.deleteBonusSettlementSupplier(
            params.bonusSettlementId,
            params.bonusSettlementSupplierId
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }
        thunkAPI.dispatch(
            Snackbar.enqueue(
                PoolNotification.success('app.bonusSettlementsSupplierOverview.action.removeSupplier.success')
            )
        );
        return params.bonusSettlementSupplierId;
    }
);

export const reclaimSupplier = createAsyncThunk(
    'bonus-settlements/reclaimSuppliers',
    async (params: { bonusSettlementId: number; bonusSettlementSupplierId: number }, thunkAPI) => {
        const response = await api.postReclaimBonusSettlementSuppliers(
            params.bonusSettlementId,
            params.bonusSettlementSupplierId
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }
        return params.bonusSettlementSupplierId;
    }
);

export const revertReclaimSupplier = createAsyncThunk(
    'bonus-settlements/revertReclaimSuppliers',
    async (params: { bonusSettlementId: number; bonusSettlementSupplierId: number }, thunkAPI) => {
        const response = await api.postRevertReclaimBonusSettlementSuppliers(
            params.bonusSettlementId,
            params.bonusSettlementSupplierId
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }
        return params.bonusSettlementSupplierId;
    }
);

export const reopenSupplier = createAsyncThunk(
    'bonus-settlements/reopenSuppliers',
    async (params: { bonusSettlementId: number; bonusSettlementSupplierId: number }, thunkAPI) => {
        const response = await api.postReopenBonusSettlementSupplier(
            params.bonusSettlementId,
            params.bonusSettlementSupplierId
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }
        return params.bonusSettlementSupplierId;
    }
);

export const remindSuppliers = createAsyncThunk(
    'bonus-settlements/remindSuppliers',
    async (
        params: {
            bonusSettlementId: number;
            remindAll?: boolean;
            bonusSettlementSupplierIds?: number[];
            supplierName?: string;
            supplierCount?: number;
        },
        thunkAPI
    ) => {
        const response = await api.postRemindBonusSettlementSuppliers(
            params.bonusSettlementId,
            params.remindAll,
            params.bonusSettlementSupplierIds
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }
        thunkAPI.dispatch(
            Snackbar.enqueue(
                PoolNotification.success(
                    params.remindAll
                        ? 'app.bonusSettlementsSupplierOverview.action.remindAllSuppliers.success'
                        : 'app.bonusSettlementsSupplierOverview.action.remindSupplier.success',
                    { number: params.supplierCount, supplier: params.supplierName }
                )
            )
        );
        return params;
    }
);

export const completeSuppliers = createAsyncThunk(
    'bonus-settlements/completeSuppliers',
    async (
        params: {
            bonusSettlementId: number;
            completeAll?: boolean;
            bonusSettlementSupplierIds?: number[];
            supplierName?: string;
            supplierCount?: number;
        },
        thunkAPI
    ) => {
        const response = await api.postCompleteBonusSettlementSuppliers(
            params.bonusSettlementId,
            params.completeAll,
            params.bonusSettlementSupplierIds
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(
                PoolNotification.success(
                    params.completeAll
                        ? 'app.bonusSettlementsSupplierOverview.action.completeAll.success'
                        : 'app.bonusSettlementsSupplierOverview.action.completeSupplier.success',
                    { number: params.supplierCount, supplier: params.supplierName }
                )
            )
        );

        return params;
    }
);
